/**
 * StaticContentAdminSection · iter168 (2026-02-27)
 *
 * Verwaltet:
 *  - News + FAQ pro Bereich (MARKETPLACE | STILLTOUCHEDX | SOCIAL)
 *  - Impressum / AGB / Datenschutz (global, kein Bereich)
 *
 * UI-Pattern angelehnt an PlatformPost-Editor.
 */
import { useEffect, useState, useCallback } from "react";
import api, { formatError } from "@/lib/api";
import RichTextEditor from "@/components/common/RichTextEditor";
import { Plus, Trash2, Save, Eye, EyeOff, Pencil, X, Pin, Camera, Loader2 } from "lucide-react";
import FeedStyleArticles from "@/components/common/FeedStyleArticles";
import AIAssistBar from "@/components/ai/AIAssistBar";
import ImageCropDialog from "@/components/home/ImageCropDialog";

const AREAS = [
  { key: "MARKETPLACE", label: "Marketplace" },
  { key: "STILLTOUCHEDX", label: "stilltouchedX" },
  { key: "SOCIAL", label: "Social" },
];
const KINDS = [
  { key: "NEWS", label: "News" },
  { key: "FAQ", label: "FAQ" },
];
const STATIC_PAGES = [
  { key: "IMPRESSUM", label: "Impressum" },
  { key: "AGB", label: "AGB" },
  { key: "PRIVACY", label: "Datenschutz" },
];

export default function StaticContentAdminSection() {
  const [mainTab, setMainTab] = useState("articles"); // articles | static
  return (
    <div className="space-y-4" data-testid="static-content-admin">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#0A0A0A]">News / FAQ / Rechtliches</h2>
        <div className="flex gap-1 border border-[#E5E5E5] rounded-full p-1 bg-white">
          <button data-testid="static-tab-articles" onClick={() => setMainTab("articles")}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition ${mainTab === "articles" ? "bg-[#0A0A0A] text-white" : "text-[#666] hover:bg-[#F5F5F5]"}`}>
            News + FAQ
          </button>
          <button data-testid="static-tab-static" onClick={() => setMainTab("static")}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition ${mainTab === "static" ? "bg-[#0A0A0A] text-white" : "text-[#666] hover:bg-[#F5F5F5]"}`}>
            Impressum · AGB · Datenschutz
          </button>
        </div>
      </div>
      {mainTab === "articles" ? <ArticlesEditor /> : <StaticPagesEditor />}
    </div>
  );
}

// ─── Articles Editor (News + FAQ pro Bereich) ────────────────────
function ArticlesEditor() {
  const [area, setArea] = useState("MARKETPLACE");
  const [kind, setKind] = useState("NEWS");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // article_id or "new"

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/admin/articles?area=${area}&kind=${kind}`);
      setItems(data.items || []);
    } catch (e) { setError(formatError(e)); }
    finally { setLoading(false); }
  }, [area, kind]);
  useEffect(() => { load(); }, [load]);

  const del = async (aid) => {
    if (!window.confirm("Wirklich löschen?")) return;
    try {
      await api.delete(`/admin/articles/${aid}`);
      await load();
    } catch (e) { setError(formatError(e)); }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        <div className="flex gap-1 border border-[#E5E5E5] rounded-full p-1 bg-white">
          {AREAS.map((a) => (
            <button key={a.key} data-testid={`article-area-${a.key}`} onClick={() => setArea(a.key)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition ${area === a.key ? "bg-[#0A0A0A] text-white" : "text-[#666] hover:bg-[#F5F5F5]"}`}>
              {a.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1 border border-[#E5E5E5] rounded-full p-1 bg-white">
          {KINDS.map((k) => (
            <button key={k.key} data-testid={`article-kind-${k.key}`} onClick={() => setKind(k.key)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition ${kind === k.key ? "bg-[#0A0A0A] text-white" : "text-[#666] hover:bg-[#F5F5F5]"}`}>
              {k.label}
            </button>
          ))}
        </div>
        <button data-testid="article-new-btn" onClick={() => setEditing("new")}
          className="ml-auto px-3 py-1.5 rounded-full bg-[#0A0A0A] text-white text-xs font-semibold hover:bg-neutral-800 transition flex items-center gap-1">
          <Plus size={14} /> Neu
        </button>
      </div>
      {error && <div className="mb-2 p-2 bg-red-50 text-red-700 text-xs rounded">{error}</div>}
      {loading && <div className="text-sm text-[#666]">Lade…</div>}
      {!loading && items.length === 0 && !editing && (
        <div className="p-6 rounded-xl border border-dashed border-[#E5E5E5] text-center text-[#666] text-sm">
          Noch keine Einträge — klick auf &bdquo;Neu&ldquo; um den ersten Eintrag anzulegen.
        </div>
      )}
      <div className="space-y-2">
        {items.map((a) => (
          <div key={a.article_id} data-testid={`article-item-${a.article_id}`}
            className="p-3 rounded-xl border border-[#E5E5E5] bg-white flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-[#0A0A0A] truncate">{a.title}</span>
                {a.is_pinned && (
                  <span data-testid={`article-pinned-badge-${a.article_id}`}
                    className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 uppercase font-bold flex items-center gap-0.5">
                    <Pin size={10} /> Pin
                  </span>
                )}
                {!a.published && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 uppercase font-bold">
                    Entwurf
                  </span>
                )}
                <span className="text-[10px] text-[#999]">#{a.sort_order}</span>
              </div>
              <div className="text-xs text-[#666] mt-1">
                {new Date(a.updated_at).toLocaleDateString("de-DE")} · {(a.body || "").replace(/<[^>]+>/g, "").slice(0, 120)}…
              </div>
            </div>
            <button data-testid={`article-edit-${a.article_id}`} onClick={() => setEditing(a.article_id)}
              className="p-1.5 rounded-full hover:bg-[#F5F5F5]" title="Bearbeiten">
              <Pencil size={14} />
            </button>
            <button data-testid={`article-delete-${a.article_id}`} onClick={() => del(a.article_id)}
              className="p-1.5 rounded-full hover:bg-red-50 text-red-500" title="Löschen">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
      {editing && (
        <ArticleEditModal
          area={area}
          kind={kind}
          articleId={editing === "new" ? null : editing}
          onClose={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ArticleEditModal({ area, kind, articleId, onClose }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [published, setPublished] = useState(true);
  const [isPinned, setIsPinned] = useState(false);
  const [mediaUrl, setMediaUrl] = useState(""); // iter169.4 · nur NEWS
  const [platformAvatar, setPlatformAvatar] = useState("");
  const [cropFile, setCropFile] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isNews = kind === "NEWS";

  // iter169.5 · Platform-Avatar (Company-Avatar) für Live-Preview holen
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/content-updates");
        if (mounted) setPlatformAvatar(data.platform_avatar_url || "");
      } catch { /* silent */ }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!articleId) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/articles?area=${area}&kind=${kind}`);
        const doc = (data.items || []).find((i) => i.article_id === articleId);
        if (doc) {
          setTitle(doc.title || "");
          setBody(doc.body || "");
          setSortOrder(doc.sort_order || 0);
          setPublished(!!doc.published);
          setIsPinned(!!doc.is_pinned);
          setMediaUrl(doc.media_url || "");
        }
      } catch (e) { setError(formatError(e)); }
    })();
  }, [articleId, area, kind]);

  const areaScope = area === "STILLTOUCHEDX" ? "STX" : area;

  const uploadCropped = async (blob) => {
    setUploadBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", blob, blob.name || "news.jpg");
      const { data } = await api.post(`/upload?area_scope=${areaScope}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMediaUrl(`/api/files/${data.path}`);
    } catch (e) {
      setError(formatError(e) || "Upload fehlgeschlagen");
    } finally { setUploadBusy(false); }
  };

  const save = async () => {
    if (!title.trim()) { setError("Titel ist Pflicht"); return; }
    if (!body.trim() || body === "<p></p>") { setError("Inhalt ist Pflicht"); return; }
    setSaving(true); setError("");
    try {
      const payload = { title, body, sort_order: sortOrder, published, is_pinned: isPinned };
      if (isNews) payload.media_url = mediaUrl || null;
      if (articleId) {
        await api.patch(`/admin/articles/${articleId}`, payload);
      } else {
        await api.post(`/admin/articles`, { area, kind, ...payload });
      }
      onClose();
    } catch (e) { setError(formatError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="article-modal" className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-[#E5E5E5]">
          <h3 className="text-base font-bold text-[#0A0A0A]">
            {articleId ? "Bearbeiten" : "Neu"} — {area} · {kind}
          </h3>
          <button data-testid="article-modal-close" onClick={onClose}
            className="p-1.5 rounded-full hover:bg-[#F5F5F5]"><X size={16} /></button>
        </div>
        {/* iter169.2 · Desktop-Split: links Formular, rechts Live-Vorschau (Feed-Style). */}
        <div className="flex-1 overflow-y-auto grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#E5E5E5]">
          {/* Formular */}
          <div className="p-4 space-y-3">
            {error && <div className="p-2 bg-red-50 text-red-700 text-xs rounded">{error}</div>}
            <div>
              <label className="text-xs font-semibold text-[#666]">Titel</label>
              <input data-testid="article-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E5E5E5] text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#666]">Inhalt</label>
              <div className="mt-1 rounded-lg border border-[#E5E5E5] overflow-hidden">
                <RichTextEditor value={body} onChange={setBody} testIdSuffix="article" />
              </div>
              {/* iter169.3 · AI-Text-Assistent: Generieren / Verbessern / Kürzen / Erweitern / Umformulieren / Rechtschr. */}
              <AIAssistBar
                area={areaScope}
                value={body}
                onApply={(newText) => setBody(newText)}
                fieldType="announcement"
                testIdSuffix={`article-body-${kind.toLowerCase()}`}
              />
            </div>
            {/* iter169.4 · Bild-Upload (nur NEWS · 16:9-Crop analog PlatformPost) */}
            {isNews && (
              <div>
                <label className="text-xs font-semibold text-[#666] block mb-1">Bild (optional · 16:9)</label>
                {mediaUrl ? (
                  <div className="relative w-full max-w-md">
                    <img
                      src={mediaUrl}
                      alt="Vorschau"
                      data-testid="article-media-preview"
                      className="w-full aspect-video object-cover rounded-lg border border-[#E5E5E5] bg-[#F5F5F5]"
                    />
                    <button
                      type="button"
                      data-testid="article-media-remove"
                      onClick={() => setMediaUrl("")}
                      className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white border border-[#E5E5E5] shadow flex items-center justify-center text-red-500 hover:bg-red-50"
                      title="Bild entfernen"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <label
                      data-testid="article-media-upload"
                      className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white border border-[#0A0A0A] text-[#0A0A0A] text-xs font-medium cursor-pointer transition-colors ${
                        uploadBusy ? "opacity-50 cursor-wait" : "hover:bg-[#0A0A0A] hover:text-white"
                      }`}
                    >
                      {uploadBusy ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
                      Bild wählen
                      <input
                        type="file"
                        accept="image/*"
                        disabled={uploadBusy}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = "";
                          if (f) { setError(""); setCropFile(f); }
                        }}
                        className="hidden"
                      />
                    </label>
                    <span className="text-[10px] text-[#999]">JPG/PNG · wird auf 16:9 zugeschnitten</span>
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-xs font-semibold text-[#666] flex items-center gap-2">
                Sortier-Order
                <input data-testid="article-sort-input" type="number" value={sortOrder}
                  onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
                  className="w-20 px-2 py-1 rounded-lg border border-[#E5E5E5] text-sm" />
              </label>
              <label className="text-xs font-semibold text-[#666] flex items-center gap-2 cursor-pointer">
                <input data-testid="article-published-checkbox" type="checkbox" checked={published}
                  onChange={(e) => setPublished(e.target.checked)} />
                {published ? <><Eye size={12} /> Veröffentlicht</> : <><EyeOff size={12} /> Entwurf</>}
              </label>
              <label className="text-xs font-semibold text-[#666] flex items-center gap-2 cursor-pointer">
                <input data-testid="article-pinned-checkbox" type="checkbox" checked={isPinned}
                  onChange={(e) => setIsPinned(e.target.checked)} />
                <Pin size={12} /> {isPinned ? "Angepinnt (oben)" : "Anpinnen"}
              </label>
            </div>
          </div>
          {/* Live-Vorschau */}
          <div className="p-4 bg-[#FAFAFA]">
            <p className="text-[10px] font-bold text-[#999] uppercase tracking-wider mb-2">Live-Vorschau</p>
            <div data-testid="article-preview">
              {(!title.trim() && (!body.trim() || body === "<p></p>")) ? (
                <div className="text-xs text-[#999] italic p-4 rounded-xl border border-dashed border-[#E5E5E5] bg-white text-center">
                  Beginne mit dem Tippen, um die Vorschau zu sehen.
                </div>
              ) : (
                <FeedStyleArticles
                  area={area}
                  items={[{
                    article_id: "preview",
                    title: title || "(ohne Titel)",
                    body: body || "",
                    is_pinned: isPinned,
                    media_url: isNews ? (mediaUrl || undefined) : undefined,
                    platform_avatar_url: platformAvatar || undefined,
                    updated_at: new Date().toISOString(),
                    created_at: new Date().toISOString(),
                  }]}
                />
              )}
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-[#E5E5E5] flex justify-end gap-2">
          <button data-testid="article-cancel" onClick={onClose}
            className="px-4 py-2 rounded-full text-sm text-[#666] hover:bg-[#F5F5F5]">Abbrechen</button>
          <button data-testid="article-save" onClick={save} disabled={saving}
            className="px-4 py-2 rounded-full bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-50 flex items-center gap-1">
            <Save size={14} /> {saving ? "Speichere…" : "Speichern"}
          </button>
        </div>
      </div>
      {/* iter169.4 · 16:9-Crop-Dialog analog PlatformPost */}
      {cropFile && (
        <ImageCropDialog
          file={cropFile}
          aspect={16 / 9}
          title="News-Bild zuschneiden (16:9)"
          onCancel={() => setCropFile(null)}
          onCropped={async (blob) => {
            setCropFile(null);
            await uploadCropped(blob);
          }}
        />
      )}
    </div>
  );
}

// ─── Company-Settings Editor (Logo für offizielle Platform-Posts) ────
function CompanySettingsEditor() {
  const [avatar, setAvatar] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/platform/company");
        setAvatar(data.avatar_url || "");
      } catch (e) { setError(formatError(e)); }
    })();
  }, []);

  const uploadCropped = async (blob) => {
    setUploading(true); setError(""); setOk(false);
    try {
      const fd = new FormData();
      fd.append("file", blob, "company-logo.jpg");
      const { data } = await api.post("/upload?area_scope=SOCIAL", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = `/api/files/${data.path}`;
      setAvatar(url);
      await api.put("/admin/platform/company", { avatar_url: url });
      setOk(true);
    } catch (e) { setError(formatError(e)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true); setError(""); setOk(false);
    try {
      await api.put("/admin/platform/company", { avatar_url: avatar });
      setOk(true);
    } catch (e) { setError(formatError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="company-settings-editor" className="space-y-4">
      <p className="text-xs text-[#666] max-w-2xl">
        Das Company-Logo erscheint als Avatar in allen offiziellen stilltouched-Beiträgen und in News-Posts.
        Empfohlen: quadratisches PNG/JPG, min. 512×512 px.
      </p>
      {error && <div className="p-2 bg-red-50 text-red-700 text-xs rounded">{error}</div>}
      {ok && <div className="p-2 bg-emerald-50 text-emerald-700 text-xs rounded">Gespeichert.</div>}
      <div className="flex items-center gap-4">
        <div className="w-24 h-24 rounded-full overflow-hidden bg-[#F5F5F5] border border-[#E5E5E5] shrink-0 flex items-center justify-center text-2xl font-bold text-[#999]">
          {avatar ? <img src={avatar} alt="Company" className="w-full h-full object-cover" data-testid="company-avatar-preview" /> : "s"}
        </div>
        <div className="flex flex-col gap-2">
          <label
            data-testid="company-avatar-upload"
            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white border border-[#0A0A0A] text-[#0A0A0A] text-xs font-medium cursor-pointer transition-colors ${
              uploading ? "opacity-50 cursor-wait" : "hover:bg-[#0A0A0A] hover:text-white"
            }`}
          >
            {uploading ? "Lade hoch…" : "Neues Logo hochladen (1:1)"}
            <input
              type="file"
              accept="image/*"
              disabled={uploading}
              onChange={(e) => {
                const f = e.target.files?.[0]; e.target.value = "";
                if (f) { setError(""); setOk(false); setCropFile(f); }
              }}
              className="hidden"
            />
          </label>
          {avatar && (
            <button data-testid="company-avatar-clear" onClick={() => { setAvatar(""); save(); }}
              className="text-xs text-red-500 hover:underline text-left">
              Logo entfernen
            </button>
          )}
        </div>
      </div>
      {cropFile && (
        <ImageCropDialog
          file={cropFile}
          aspect={1}
          title="Company-Logo zuschneiden (1:1)"
          onCancel={() => setCropFile(null)}
          onCropped={async (blob) => { setCropFile(null); await uploadCropped(blob); }}
        />
      )}
      <div className="text-[10px] text-[#999]">
        Letztes Speichern manuell nötig? {" "}
        <button onClick={save} disabled={saving}
          className="underline hover:no-underline">
          {saving ? "Speichere…" : "Jetzt speichern"}
        </button>
      </div>
    </div>
  );
}

// ─── Static Pages Editor (global) ────────────────────────────────
function StaticPagesEditor() {
  const [activeKind, setActiveKind] = useState("IMPRESSUM");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/admin/static-pages/${activeKind}`);
      setBody(data.body || "");
      setSavedAt(data.updated_at);
    } catch (e) { setError(formatError(e)); }
    finally { setLoading(false); }
  }, [activeKind]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setError("");
    try {
      const { data } = await api.put(`/admin/static-pages/${activeKind}`, { body });
      setSavedAt(data.updated_at);
    } catch (e) { setError(formatError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex gap-1 border border-[#E5E5E5] rounded-full p-1 bg-white mb-3 w-fit">
        {STATIC_PAGES.map((p) => (
          <button key={p.key} data-testid={`static-kind-${p.key}`} onClick={() => setActiveKind(p.key)}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition ${activeKind === p.key ? "bg-[#0A0A0A] text-white" : "text-[#666] hover:bg-[#F5F5F5]"}`}>
            {p.label}
          </button>
        ))}
      </div>
      {error && <div className="mb-2 p-2 bg-red-50 text-red-700 text-xs rounded">{error}</div>}
      {loading ? <div className="text-sm text-[#666]">Lade…</div> : (
        <>
          <div className="rounded-xl border border-[#E5E5E5] overflow-hidden">
            <RichTextEditor value={body} onChange={setBody} testIdSuffix={`static-${activeKind}`} />
          </div>
          <div className="mt-3 flex items-center gap-3">
            <button data-testid="static-save-btn" onClick={save} disabled={saving}
              className="px-4 py-2 rounded-full bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-50 flex items-center gap-1">
              <Save size={14} /> {saving ? "Speichere…" : "Speichern"}
            </button>
            {savedAt && (
              <span className="text-xs text-[#666]">
                Letzte Änderung: {new Date(savedAt).toLocaleString("de-DE")}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
